import {Button as HeadlessUiButton, Transition} from "@headlessui/react";
import type {ReactNode} from "react";
import {twJoin} from "tailwind-merge";
import {Spinner} from "@vesta/aergia/components/spinner";
import {tw} from "@vesta/aergia/utilities/utilities";

// TODO: Handle icons
// TODO: The loader appear/exit animations differ from the ones used in ADS
export function Button({
	appearance,
	disabled,
	isSelected,
	isLoading,
	spacing,
	shouldFitContainer,
	iconBefore,
	iconAfter,
	children,
	onClick,
	className,
	type = "button",
	testId,
}: {
	appearance?: "danger" | "default" | "discovery" | "primary" | "subtle" | "warning" | "information";
	disabled?: boolean;
	isSelected?: boolean;
	isLoading?: boolean;
	spacing?: "default" | "compact";
	shouldFitContainer?: boolean;
	iconBefore?: ReactNode;
	iconAfter?: ReactNode;
	children: ReactNode;
	onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
	className?: string;
	type?: "button" | "submit";
	testId?: string;
}) {
	// TODO: Not sure if this is a good idea, but doing this for easy styling for now
	if (isLoading === true) {
		disabled = false;
	}

	const backgroundClassName =
		disabled === true
			? tw`bg-ds-background-disabled`
			: isSelected === true
				? tw`bg-ds-background-selected`
				: appearance === "danger"
					? tw`bg-ds-background-danger-bold`
					: appearance === "discovery"
						? tw`bg-ds-background-discovery-bold`
						: appearance === "primary"
							? tw`bg-ds-background-brand-bold`
							: appearance === "subtle"
								? tw`bg-ds-background-neutral-subtle`
								: appearance === "warning"
									? tw`bg-ds-background-warning-bold`
									: appearance === "information"
										? tw`bg-ds-background-neutral-subtle`
										: tw`bg-ds-background-neutral-subtle`;
	const textClassName =
		disabled === true
			? tw`text-ds-text-disabled`
			: isSelected === true
				? tw`text-ds-text-selected`
				: appearance === "danger"
					? tw`text-ds-text-inverse`
					: appearance === "discovery"
						? tw`text-ds-text-inverse`
						: appearance === "primary"
							? tw`text-ds-text-inverse`
							: appearance === "subtle"
								? tw`text-ds-text-subtle`
								: appearance === "warning"
									? tw`text-ds-text-inverse`
									: appearance === "information"
										? tw`text-ds-text-discovery`
										: tw`text-ds-text-subtle`;
	const cursorClassName = isLoading === true ? tw`cursor-progress` : disabled === true ? tw`cursor-not-allowed` : null;
	const hoverBackgroundClassName =
		isLoading === true
			? null
			: disabled === true
				? null
				: isSelected === true
					? tw`hover:bg-ds-background-selected-hovered`
					: appearance === "danger"
						? tw`hover:bg-ds-background-danger-bold-hovered`
						: appearance === "discovery"
							? tw`hover:bg-ds-background-discovery-bold-hovered`
							: appearance === "primary"
								? tw`hover:bg-ds-background-brand-bold-hovered`
								: appearance === "subtle"
									? tw`hover:bg-ds-background-neutral-subtle-hovered`
									: appearance === "warning"
										? tw`hover:bg-ds-background-warning-bold-hovered`
										: appearance === "information"
											? tw`hover:bg-ds-background-neutral-subtle-hovered`
											: tw`hover:bg-ds-background-neutral-subtle-hovered`;
	const activeBackgroundClassName =
		isLoading === true
			? null
			: disabled === true
				? null
				: isSelected === true
					? tw`active:bg-ds-background-selected-pressed`
					: appearance === "danger"
						? tw`active:bg-ds-background-danger-bold-pressed`
						: appearance === "discovery"
							? tw`active:bg-ds-background-discovery-bold-pressed`
							: appearance === "primary"
								? tw`active:bg-ds-background-brand-bold-pressed`
								: appearance === "subtle"
									? tw`active:bg-ds-background-neutral-subtle-pressed`
									: appearance === "warning"
										? tw`active:bg-ds-background-warning-bold-pressed`
										: appearance === "information"
											? tw`active:bg-ds-background-neutral-subtle-pressed`
											: tw`active:bg-ds-background-neutral-subtle-pressed`;

	// TODO: Figure out a better way of doing this?
	// Maybe using `::after` for consistency with ADS?
	const borderClassName =
		isSelected === true
			? tw`outline-ds-border-width outline-solid outline-ds-border-selected -outline-offset-ds-border-width`
			: appearance === "danger"
				? null
				: appearance === "discovery"
					? null
					: appearance === "primary"
						? null
						: appearance === "subtle"
							? null
							: appearance === "warning"
								? null
								: appearance === "information"
									? tw`outline-ds-border-width outline-solid outline-ds-border-discovery -outline-offset-ds-border-width`
									: tw`outline-ds-border-width outline-solid outline-ds-border -outline-offset-ds-border-width`;
	const pxClassName = tw`px-ds-space-150`;
	const pyClassName = spacing === "compact" ? tw`py-ds-space-025` : tw`py-ds-space-075`;
	// Adding max-w-full just in case (css behaves weirdly at times)
	// (specific case I'm fixing with this: `div[w=5rem] > button` - here button was
	// not truncating at 5rem as expected)
	const widthClassName = shouldFitContainer === true ? tw`w-full mx-auto` : tw`w-fit max-w-full`;
	// Not sure if this is necessary
	const heightClassName = spacing === "compact" ? tw`h-[1.5rem]` : tw`h-[2rem]`;
	const spinnerSizeClassName = spacing === "compact" ? tw`w-4 h-4` : tw`w-6 h-6`;

	// column-gap: var(--ds-space-050, 4px);
	return (
		<HeadlessUiButton
			disabled={disabled}
			type={type}
			onClick={onClick}
			data-testid={testId}
			className={twJoin(
				widthClassName,
				heightClassName,
				pxClassName,
				pyClassName,
				backgroundClassName,
				textClassName,
				cursorClassName,
				hoverBackgroundClassName,
				activeBackgroundClassName,
				borderClassName,
				"text-ds-font-body",
				// TODO: This should not require an important. Figure this out.
				"!font-ds-font-weight-medium",
				"rounded-ds-border-radius-100",
				"transition-colors duration-small",
				// "overflow-hidden",
				"relative",
				className,
			)}
		>
			{/* break-all */}
			<div
				className={twJoin(
					"line-clamp-1",
					"transition-opacity duration-medium",
					isLoading === true ? "opacity-0" : "opacity-100",
					(Boolean(iconBefore) || Boolean(iconAfter)) && "flex items-center gap-ds-space-050 w-full",
				)}
			//
			>
				{Boolean(iconBefore) && <span className="cursor-text pr-ds-space-025">{iconBefore}</span>}
				<div>{children}</div>
				{Boolean(iconAfter) && <span className="cursor-text pr-ds-space-025">{iconAfter}</span>}
			</div>

			<Transition show={isLoading === true}>
				<div
					className={twJoin("absolute inset-0", "grid place-items-center", "transition-opacity duration-medium", "opacity-100 data-closed:opacity-0")}
				//
				>
					<Spinner className={spinnerSizeClassName} />
				</div>
			</Transition>
		</HeadlessUiButton>
	);
}
